import GridCard from '@Guidelines/Component/GridCard';
import { theme } from '@Guidelines/Theme/theme';
import StaffMemberCardActions from '@UI/Pages/Agencies/StaffMembers/StaffMemberCard/StaffMemberCardActions';
import useOrganizationFacilities from '@UI/Pages/Agencies/StaffMembers/Hooks/useOrganizationFacilities';
import resolveFacilityNames from '@UI/Helpers/resolveFacilityNames';
import type StaffMemberView from '@UI/Store/StaffMembers/Views/StaffMemberView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Tag } from '@verclam/ui-kit';

const STAFF_MEMBER_CARD_TRACK_COUNT = 4;

const nameStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const detailStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

const detailsStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
`;

type Props = {
	readonly staffMember: StaffMemberView;
};

/**
 * One staff member, as an `itemTemplate` cell of the `DataView` grid, laid out as a four-track
 * subgrid: name, job function label (never hardcoded, always `jobFunction.label` from the server), a
 * details block, and actions. The details block stacks the agencies they belong to and their phone
 * number when known, each omitted when absent. The server only sends `facilityIds`: agency names are
 * resolved from `useOrganizationFacilities`. An id with no resolved name is dropped, never shown as a
 * bare number.
 */
const StaffMemberCard: React.FC<Props> = ({ staffMember }) => {
	const { data: facilities } = useOrganizationFacilities();
	const facilityNames = React.useMemo(
		() => resolveFacilityNames(staffMember.facilityIds, facilities?.list ?? []),
		[staffMember.facilityIds, facilities],
	);

	return (
		<GridCard trackCount={STAFF_MEMBER_CARD_TRACK_COUNT} rowGap="0.5rem">
			<h2 css={nameStyle}>{staffMember.fullName}</h2>
			<Tag value={staffMember.jobFunction.label} severity="secondary" />
			<div css={detailsStyle}>
				{facilityNames.length > 0 && <p css={detailStyle}>{facilityNames.join(', ')}</p>}
				{staffMember.phoneNumber !== null && <p css={detailStyle}>{staffMember.phoneNumber}</p>}
			</div>
			<StaffMemberCardActions staffMember={staffMember} />
		</GridCard>
	);
};

export default StaffMemberCard;
